import { Age } from './choices/epidemiology/age';
import { Geography } from './choices/epidemiology/geography';
import { Measured } from './choices/epidemiology/measured';
import { Rarity } from './choices/epidemiology/rarity';
import { Sex } from './choices/sex';

/**
 * Constructor options for EpidemiologySexAgeDetail.
 */
export interface EpidemiologySexAgeDetailConstructorOpts {
  sex: Sex;
  age: Age;
  rate: number;
}

/**
 * Epidemology with incidence or prevalence given as x/100.000 citizen
 * for a given sex (male or female) at a specific age.
 */
export class EpidemiologySexAgeDetail {
  public sex: Sex;
  public age: Age;
  public rate: number;

  constructor(opts: EpidemiologySexAgeDetailConstructorOpts) {
    this.sex = opts.sex;
    this.age = opts.age;
    this.rate = opts.rate;
  }

  toString() {
    return (
      '<EpidemiologySexAgeDetail: sex=' +
      this.sex +
      ', age=' +
      this.age +
      ', rate=' +
      this.rate +
      '>'
    );
  }

  copyWith({
    sex = undefined,
    age = undefined,
    rate = undefined
  }: {
    sex?: Sex;
    age?: Age;
    rate?: number;
  } = {}): EpidemiologySexAgeDetail {
    return new EpidemiologySexAgeDetail({
      sex: sex ?? this.sex,
      age: age ?? this.age,
      rate: rate ?? this.rate
    });
  }

  toJson() {
    return {
      sex: this.sex.id,
      age: this.age.id,
      rate: this.rate
    };
  }

  static fromJson(json: any): EpidemiologySexAgeDetail {
    const opts: EpidemiologySexAgeDetailConstructorOpts = {
      sex: Sex.fromId(json.sex),
      age: Age.fromTag(json.age),
      rate: json.rate
    };

    return new EpidemiologySexAgeDetail(opts);
  }
}

/**
 * Constructor options for Epidemiology.
 */
export interface EpidemiologyConstructorOpts {
  id?: number;
  order?: number;

  epidemiology: Measured;
  location: Geography[];
  rarity: Rarity;
  ages?: EpidemiologySexAgeDetail[];
}

/**
 * Epidemiology specification for a specific rari
 */
export class Epidemiology {
  // ID and ordering
  public readonly id?: number;
  public order: number;

  // Epidemiology data
  public epidemiology: Measured;
  public location: Geography[];
  public rarity: Rarity;
  public ages: EpidemiologySexAgeDetail[];

  constructor(opts: EpidemiologyConstructorOpts) {
    // ID and ordering
    this.id = opts.id;
    this.order = opts.order || 1;
    // Epidemiology data
    this.epidemiology = opts.epidemiology;
    this.location = opts.location;
    this.rarity = opts.rarity;
    this.ages = opts.ages || [];
  }

  isDetailed(): boolean {
    return this.rarity === Rarity.NONE;
  }

  isDefault(): boolean {
    return this.location.length === 1 && this.location[0] === Geography.DEFAULT;
  }

  toString() {
    return (
      '<Epidemiology: ' +
      'order=' +
      this.order +
      ', epidemiology=' +
      this.epidemiology +
      ', location=[' +
      this.location?.map((loc) => loc.id).join(', ') +
      ']' +
      ', rarity=' +
      this.rarity?.label +
      ', ages=[' +
      this.ages.map((age) => age.toJson()) +
      ']>'
    );
  }

  toJson(): any {
    return {
      id: this.id,
      order: this.order,
      epidemiology: this.epidemiology.tag,
      location: this.location.map((l) => l.id),
      rarity: this.rarity.id,
      ages: this.ages.map((r) => r.toJson())
    };
  }

  static fromJson(json: any): Epidemiology {
    const opts: EpidemiologyConstructorOpts = {
      id: json.id || undefined,
      order: json.order || undefined,
      epidemiology: Measured.fromTag(json.epidemiology),
      location: json.location.map((loc: any) => Geography.fromId(loc)),
      rarity: json.rarity ? Rarity.fromTag(json.rarity) : Rarity.NONE,
      ages: Array.isArray(json.ages)
        ? json.ages.map((detail: any) => EpidemiologySexAgeDetail.fromJson(detail))
        : []
    };

    return new Epidemiology(opts);
  }
}
